/* eslint-disable react/no-unused-prop-types */
import type { AnchorHTMLAttributes, FunctionComponent } from 'react';
import classNames from 'classnames';

// Hooks
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';

// Components
import CTAButton, { CTAColorVariant } from '~/components/shared/CTAButton';
import Image from 'next-export-optimize-images/image';
import { useVariantIds } from '~/lib/hooks/useVariantIds';

// Data
import { DEFAULT_PROPS } from './data';

interface CTABannerProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  headline?: string;
  disclaimer?: string;
  CTAButtonText?: string;
  CTAButtonVariant?: CTAColorVariant;
  CTAButtonLink?: string;
  showImageMobile?: boolean;
  image?: {
    imageDesktop: string;
  };
  imageAltText?: string;
  secondaryCTAButtonText?: string;
  secondaryCTAButtonVariant?: CTAColorVariant;
  secondaryCTAButtonLink?: string;
  hasSecondaryCTAButton?: boolean;
}

// Static classNames that don't depend on props or dark mode
const contentWrapClasses = classNames(
  'content__wrap flex flex-col gap-2.5',
  'mt-8 text-center',
  'xl:mt-0 xl:ml-8 xl:text-left'
);

const ctaWrapClasses = classNames(
  'flex flex-col gap-2',
  'mt-8 w-full md:w-auto xl:mt-0 xl:ml-8'
);

const PromotionCTABanner: FunctionComponent<CTABannerProps> = () => {
  const { data } = useMonarchSlotContext<typeof DEFAULT_PROPS>();
  const { isDarkMode } = useVariantIds();

  const {
    headline,
    disclaimer,
    CTAButtonText,
    CTAButtonVariant,
    CTAButtonLink,
    hasSecondaryCTAButton,
    secondaryCTAButtonText,
    secondaryCTAButtonVariant,
    secondaryCTAButtonLink,
    imageDesktop,
    imageAltText,
  } = data ?? DEFAULT_PROPS;

  // Classes that depend on dark mode
  const containerClasses = classNames(
    'promotion-cta-banner__wrapper',
    'flex flex-col justify-center items-center text-center',
    'py-12 px-4',
    'md:px-16 xl:flex-row',
    isDarkMode ? 'bg-dark-mode-background' : 'bg-atmospheric-white'
  );

  const headlineClasses = classNames(
    'text-2xl leading-7',
    isDarkMode ? 'text-white' : 'text-ozark-noir'
  );

  const disclaimerClasses = classNames(
    'text-[10px]',
    isDarkMode ? 'text-white' : 'text-ozark-noir'
  );

  const secondaryCtaClasses = classNames(
    secondaryCTAButtonVariant === 'text-link' && isDarkMode
      ? 'text-white'
      : 'text-ozark-noir'
  );

  const baseUrl = process.env.baseUrl;
  const imgSrc = isDarkMode ? 'paramount_v2.svg' : imageDesktop;
  const imgPath = `${baseUrl}/images/sections/hero/${imgSrc}`;

  return (
    <div id='promotion-banner' className={containerClasses}>
      {/* Image */}
      <Image
        src={imgPath}
        alt={imageAltText}
        width={300}
        height={47}
        priority
      />

      {/* Headline & Disclaimer */}
      <div className={contentWrapClasses}>
        <h2 className={headlineClasses}>{headline}</h2>
        <p className={disclaimerClasses}>{disclaimer}</p>
      </div>

      {/* CTA Wrap */}
      <div className={ctaWrapClasses}>
        <CTAButton
          variant={CTAButtonVariant}
          elementPosition='BANNER_CTA_PROMOTION'
          elementType='SIGN_UP_CTA_PRIMARY'
          href={CTAButtonLink}
        >
          {CTAButtonText}
        </CTAButton>
        {hasSecondaryCTAButton && (
          <CTAButton
            elementPosition='BANNER_CTA_PROMOTION'
            variant={secondaryCTAButtonVariant}
            elementType='SIGN_UP_CTA_SECONDARY'
            href={secondaryCTAButtonLink}
            className={secondaryCtaClasses}
          >
            {secondaryCTAButtonText}
          </CTAButton>
        )}
      </div>
      {/* end cta wrap */}
    </div>
  );
};
export default PromotionCTABanner;
